import { Icon } from '@iconify-icon/react';
import Image from 'next/image';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import PageLink from '@/utils/PageLink';

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.35'/%3E%3C/svg%3E\")";

/**
 * CSS recreation of the live Faculty Portal demo (as built for Calvario City).
 * Kept as markup — crisp at every viewport, no client names.
 */
const FacultyPortalMock = () => (
  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl shadow-primary-600/10">
    {/* Browser chrome */}
    <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
      <span className="ml-3 flex-1 rounded-md bg-white px-3 py-1 text-[10px] tracking-wide text-gray-400">
        faculty.yourschool.edu
      </span>
    </div>

    {/* Portal top nav */}
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-2.5">
      <div className="flex items-baseline gap-1.5">
        <span className="font-display text-xs font-semibold italic text-gray-800">Your School</span>
        <span className="text-[9px] text-gray-300">×</span>
        <span className="text-[10px] font-bold tracking-wide text-primary-600">chat.co</span>
      </div>
      <div className="hidden items-center gap-4 text-[10px] font-medium text-gray-500 sm:flex">
        <span className="font-semibold text-primary-600">Home</span>
        <span>Manuals</span>
        <span>Ask the Assistant</span>
      </div>
      <span className="rounded-full border border-gray-200 px-2.5 py-1 text-[9px] font-medium text-gray-600">
        Sign Out
      </span>
    </div>

    {/* Portal hero */}
    <div className="relative bg-primary-600 px-5 pb-6 pt-5 sm:px-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-[52%]">
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#d3a641]" />
            <span className="text-[8px] font-bold uppercase tracking-[0.28em] text-blue-100/80">
              Your School
            </span>
          </div>
          <div className="mt-2 text-2xl font-extrabold leading-tight text-white sm:text-3xl">
            Welcome,
            <br />
            Faculty.
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-blue-100/80">
            Your digital resource center for the 2025–2026 academic year.
          </p>
          <div className="mt-3 border-t border-white/20 pt-3">
            <div className="grid grid-cols-4 gap-2">
              {[
                ['10+', 'Manuals'],
                ['25–26', 'Year'],
                ['24/7', 'Access'],
                ['AI', 'Search'],
              ].map(([v, l]) => (
                <div key={l}>
                  <div className="text-[11px] font-bold text-white">{v}</div>
                  <div className="text-[7px] text-blue-200/70">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating manuals card */}
        <div className="w-full -rotate-1 rounded-xl bg-white p-3.5 shadow-xl shadow-black/25 sm:w-[46%]">
          <div className="flex items-center gap-1.5 border-b border-gray-100 pb-2">
            <Icon icon="tabler:file-text" className="text-sm text-primary-600" />
            <span className="text-[10px] font-bold text-primary-600">Faculty Manuals 2025–26</span>
          </div>
          {['Faculty Handbook', 'HR Policy Handbook', 'Health & Safety Policy'].map((m) => (
            <div key={m} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-md bg-blue-50">
                  <Icon icon="tabler:file-type-pdf" className="text-[10px] text-primary-600" />
                </span>
                <span className="text-[10px] font-medium text-gray-700">{m}</span>
              </div>
              <Icon icon="tabler:download" className="text-[11px] text-gray-400" />
            </div>
          ))}
          <div className="border-t border-gray-100 pt-2 text-[9px] font-semibold text-primary-600">
            Browse All Manuals →
          </div>
        </div>
      </div>

      {/* Ask HR bubble */}
      <div className="absolute bottom-3 right-4 flex items-center gap-1.5 rounded-full bg-[#04182e] px-3 py-1.5 shadow-lg">
        <Icon icon="tabler:message-chatbot" className="text-xs text-white" />
        <span className="text-[9px] font-semibold text-white">Ask HR</span>
      </div>
    </div>
  </div>
);

/** Floating parent-chat card — the public agent on the school website. */
const ParentChatMock = () => (
  <div className="w-[240px] rotate-2 rounded-2xl bg-white p-3.5 shadow-2xl shadow-primary-600/20 ring-1 ring-gray-100">
    <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600">
        <Icon icon="tabler:school" className="text-xs text-white" />
      </span>
      <div>
        <div className="text-[10px] font-bold leading-tight text-gray-900">Parent Assistant</div>
        <div className="flex items-center gap-1 text-[8px] text-emerald-600">
          <span className="h-1 w-1 rounded-full bg-emerald-500" /> On your website · 24/7
        </div>
      </div>
    </div>
    <div className="ml-auto mt-2.5 w-fit max-w-[85%] rounded-xl rounded-br-sm bg-primary-600 px-2.5 py-1.5 text-[9px] leading-snug text-white">
      ¿Qué día empiezan las clases y qué debe traer mi hija?
    </div>
    <div className="mt-2 w-fit max-w-[92%] rounded-xl rounded-tl-sm bg-gray-100 px-2.5 py-1.5 text-[9px] leading-snug text-gray-700">
      Las clases comienzan el <b>11 de agosto</b>. La lista de útiles está en la página 4 del manual
      familiar<sup className="font-bold text-primary-600"> 1</sup>.
    </div>
    <div className="mt-1.5 flex items-center gap-1 text-[8px] text-gray-400">
      <Icon icon="tabler:circle-check" className="text-[9px] text-emerald-500" />
      Cited · Family Handbook, p. 4
    </div>
  </div>
);

const painPoints = [
  {
    icon: 'tabler:printer-off',
    stat: 'Thousands',
    label: 'of pages printed and re-printed every year as policies change',
  },
  {
    icon: 'tabler:phone-calling',
    stat: 'Same 20 questions',
    label: 'answered by your front office every single week — calendar, uniforms, lunch menu',
  },
  {
    icon: 'tabler:user-question',
    stat: 'Families lost',
    label: 'prospective parents visit your website after hours, find no answers, and move on',
  },
];

const audiences = [
  {
    icon: 'tabler:chalkboard-teacher',
    kicker: 'For your faculty',
    title: 'The Faculty Portal',
    body: 'A private, staff-only portal trained on your manuals and policies. Every answer cites the exact section; every original is one click away.',
    bullets: [
      'Staff-only access with email verification',
      'Cites the exact manual section',
      'Update a policy once — every answer updates',
    ],
  },
  {
    icon: 'tabler:heart-handshake',
    kicker: 'For your parents',
    title: 'A 24/7 assistant on your website',
    body: 'Calendar dates, uniform rules, pick-up procedures, lunch menus — answered instantly on your site or WhatsApp, in the language each family speaks.',
    bullets: [
      'Answers in any language, from your documents',
      'Works on your website, WhatsApp & QR codes',
      'Your front office gets its mornings back',
    ],
  },
  {
    icon: 'tabler:door-enter',
    kicker: 'For prospective families',
    title: 'An admissions agent that never sleeps',
    body: 'Tuition, requirements, tours, enrollment deadlines — answered the moment a family is curious, with their contact details captured for your admissions team.',
    bullets: [
      'Answers admissions questions after hours',
      'Captures name, email & phone as leads',
      'Turns website visits into enrollment conversations',
    ],
  },
];

const steps = [
  {
    numeral: '1',
    title: 'Send us your manuals',
    body: 'Handbooks, policies, curriculum guides, procedures — PDF, Word, or straight from Google Drive. That is genuinely all we need.',
  },
  {
    numeral: '2',
    title: 'We build your agents',
    body: 'Your branded Faculty Portal, plus a public assistant on your website for parents and prospective families — your name, your colors, live in days, not semesters.',
  },
  {
    numeral: '3',
    title: 'Everyone just asks',
    body: 'Teachers get cited policy answers. Parents get calendar and uniform answers in their language. Prospective families get admissions answers at midnight — and you get their contact info in the morning.',
  },
];

const faqs = [
  {
    q: 'What happens when a policy changes?',
    a: 'You update the document once — every answer updates instantly, and faculty can always download the current original. No reprinting, no stale copies in circulation.',
  },
  {
    q: 'Who sees what?',
    a: 'You decide per agent. The Faculty Portal is invite-only for your staff, with email verification. The parent and admissions assistants are public on your website — trained only on the documents you choose to share with families.',
  },
  {
    q: 'Does it work in Spanish or other languages?',
    a: 'Yes. Teachers, parents, and prospective families can each ask in their own language and get answers in that language — even if your manuals are written in another.',
  },
  {
    q: 'Can it really help with enrollment?',
    a: 'The admissions agent answers tuition, requirements, and deadline questions the moment a family is curious — including nights and weekends — and captures their name, email, and phone so your admissions team can follow up.',
  },
  {
    q: 'How long does setup take and what does it cost?',
    a: 'Most school portals are live within days of receiving the documents. Start a 14-day free trial to try the platform — then book a demo and we will scope your custom portal together.',
  },
];

const SchoolsPage = () => {
  return (
    <div className="bg-white">
      <NextSeo
        title="Chat.co for Schools — AI for Faculty, Parents & Admissions"
        description="One AI platform for your whole school: a branded Faculty Portal for teachers, a 24/7 website assistant for parents in any language, and an admissions agent that captures enrollment leads. Built for Calvario City Christian Academy — and next for your school."
        canonical="https://chat.co/schools"
      />
      <Header />

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#0047900a_1px,transparent_1px),linear-gradient(to_bottom,#0047900a_1px,transparent_1px)] bg-[size:3.5rem_3.5rem]" />
          <div className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-blue-100/40 blur-[100px]" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-12 sm:px-6 md:pt-16 lg:px-8 lg:pb-20">
          <div className="flex flex-col gap-12 lg:flex-row lg:items-center lg:gap-14">
            <div className="flex-1 lg:max-w-[46%]">
              <div className="flex items-center gap-4">
                <span className="h-px w-10 bg-primary-600/50" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-600">
                  Chat.co for Schools
                </span>
              </div>
              <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-gray-900 sm:text-5xl lg:text-[3.4rem]">
                Your whole school,{' '}
                <span className="font-display font-medium italic text-primary-600">
                  one question away.
                </span>
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-gray-500">
                A branded <span className="font-medium text-gray-900">Faculty Portal</span> for your
                teachers. A <span className="font-medium text-gray-900">24/7 assistant</span> on
                your website for parents and prospective families — in their language. All trained
                on your handbooks and policies, all answering with citations.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  href={PageLink.demo}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 px-7 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/25">
                  Book a demo
                  <Icon icon="tabler:arrow-right" className="text-base" />
                </Link>
                <Link
                  href={PageLink.login}
                  className="inline-flex items-center justify-center rounded-full border border-primary-600/30 px-7 py-3 text-sm font-semibold text-primary-600 hover:bg-primary-50">
                  Start 14-Day Trial
                </Link>
              </div>
              <p className="mt-6 text-sm text-gray-400">
                Already running at{' '}
                <Link
                  href="/case-studies/calvario-academy"
                  className="font-medium text-primary-600 hover:text-primary-700">
                  Calvario City Christian Academy &amp; Pre-School →
                </Link>
              </p>
            </div>
            <div className="relative w-full flex-1 pb-16 lg:pb-0">
              <FacultyPortalMock />
              <div className="absolute -bottom-2 left-2 lg:-bottom-8 lg:-left-6">
                <ParentChatMock />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pain strip ─── */}
      <section className="border-y border-gray-100 bg-[#f6f8fb] py-14 sm:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            Still printing{' '}
            <span className="font-display font-medium italic text-primary-600">the handbook?</span>
          </h2>
          <div className="mx-auto mt-10 grid max-w-4xl gap-8 sm:grid-cols-3">
            {painPoints.map((p) => (
              <div key={p.stat} className="text-center">
                <Icon icon={p.icon} className="text-3xl text-primary-600/60" />
                <div className="mt-2 text-xl font-bold text-gray-900">{p.stat}</div>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">{p.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Audiences ─── */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-primary-600/50" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-600">
                One platform, every audience
              </span>
            </div>
            <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-[2.6rem] sm:leading-[1.15]">
              Faculty. Parents.{' '}
              <span className="font-display font-medium italic text-primary-600">
                Future families.
              </span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-500">
              The same knowledge that runs your Faculty Portal can greet every parent and
              prospective family — each audience gets its own agent, with exactly the access you
              choose.
            </p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {audiences.map((a) => (
              <div
                key={a.title}
                className="flex flex-col rounded-[28px] border border-gray-100 bg-[#f8fafc] p-8 transition-shadow duration-300 hover:shadow-md">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm">
                  <Icon icon={a.icon} className="text-2xl text-primary-600" />
                </div>
                <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.24em] text-primary-600">
                  {a.kicker}
                </div>
                <h3 className="mt-1.5 text-xl font-bold text-gray-900">{a.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-500">{a.body}</p>
                <ul className="mt-5 space-y-2 border-t border-gray-200/70 pt-4">
                  {a.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
                      <Icon
                        icon="tabler:circle-check-filled"
                        className="mt-0.5 text-base text-primary-600"
                      />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section className="bg-white pb-16 sm:pb-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-primary-600/50" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-600">
                How it works
              </span>
            </div>
            <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-[2.6rem] sm:leading-[1.15]">
              From binders to portal{' '}
              <span className="font-display font-medium italic text-primary-600">
                in three steps.
              </span>
            </h2>
          </div>
          <div className="mt-12 divide-y divide-gray-100 border-y border-gray-100">
            {steps.map((step) => (
              <div
                key={step.numeral}
                className="group grid gap-6 py-9 transition-colors duration-300 hover:bg-[#f8fafc] sm:grid-cols-12 sm:gap-8 sm:px-4">
                <div className="sm:col-span-2">
                  <span className="font-display text-6xl font-light italic leading-none text-gray-200 transition-colors duration-300 group-hover:text-primary-600/40 sm:text-7xl">
                    {step.numeral}
                  </span>
                </div>
                <div className="sm:col-span-4">
                  <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                </div>
                <div className="sm:col-span-6">
                  <p className="text-[15px] leading-relaxed text-gray-500">{step.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Photo band */}
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {[
              {
                src: '/images/landing/calvario-academy.png',
                alt: 'Teacher with students in class',
              },
              { src: '/images/landing/school-hallway.png', alt: 'Bright school hallway' },
              {
                src: '/images/landing/teacher-laptop.png',
                alt: 'Teacher using the portal on a laptop',
              },
            ].map((photo) => (
              <div key={photo.src} className="relative aspect-[3/2] overflow-hidden rounded-2xl">
                <Image src={photo.src} alt={photo.alt} fill className="object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Calvario story (navy) ─── */}
      <section className="relative overflow-hidden bg-[#04182e] py-16 sm:py-20">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:3.5rem_3.5rem]" />
          <div className="absolute -left-32 top-0 h-[360px] w-[360px] rounded-full bg-primary-600/25 blur-[110px]" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: GRAIN }} />
        </div>
        <div className="relative mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-[#d3a641]/70" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#ddb85a]/90">
              Proven in a real school
            </span>
          </div>
          <blockquote className="mt-6 font-display text-2xl font-medium italic leading-snug text-white sm:text-[2rem]">
            &ldquo;We used to print thousands of pages of teacher manuals every year. Now our
            faculty gets instant, cited answers through our own Faculty Portal.&rdquo;
          </blockquote>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-display text-lg italic text-white">Calvario City</div>
              <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-blue-200/60">
                Christian Academy &amp; Pre-School
              </div>
            </div>
            <Link
              href="/case-studies/calvario-academy"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white hover:text-[#04182e]">
              Read the full story
              <Icon icon="tabler:arrow-right" className="text-base" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Questions school leaders{' '}
            <span className="font-display font-medium italic text-primary-600">ask us.</span>
          </h2>
          <div className="mt-10 space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="bg-white pb-16 sm:pb-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[28px] bg-primary-600 px-8 py-12 text-center sm:px-12">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:3rem_3rem]" />
            </div>
            <h2 className="relative text-3xl font-bold text-white sm:text-4xl">
              Give your faculty{' '}
              <span className="font-display font-medium italic text-[#e2c06a]">
                instant answers.
              </span>
            </h2>
            <p className="relative mx-auto mt-4 max-w-xl text-base text-blue-100">
              Send us a handbook and see your school&apos;s portal — we&apos;ll build the demo
              before your next staff meeting.
            </p>
            <div className="relative mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href={PageLink.demo}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-primary-600 hover:bg-blue-50 sm:w-auto">
                Book a demo
                <Icon icon="tabler:arrow-right" className="text-base" />
              </Link>
              <Link
                href={PageLink.login}
                className="inline-flex w-full items-center justify-center rounded-full border border-white/40 px-7 py-3 text-sm font-semibold text-white hover:bg-white/10 sm:w-auto">
                Start 14-Day Trial
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SchoolsPage;
